import { useEffect, useState } from "react";
import {
  adminListEmailTemplates,
  adminUpdateEmailTemplate,
  adminResetEmailTemplate,
  adminSendTestEmail,
} from "@/lib/api";

/**
 * Human-friendly labels for each transactional email.
 * Order matches typical customer journey (welcome → order → shipped → delivered → refund) then support flows.
 */
const LOCALES = [
  { code: "pt", label: "Português (default)", flag: "🇵🇹" },
  { code: "en", label: "English",             flag: "🇬🇧" },
  { code: "es", label: "Español",             flag: "🇪🇸" },
];

const LABELS = {
  welcome:               { title: "Welcome (account)",       blurb: "Sent right after a customer creates an account (email/password OR Google)." },
  newsletter:            { title: "Newsletter welcome",      blurb: "Sent when a shopper joins the mailing list from the footer — distinct copy from the account welcome." },
  order_confirmed:       { title: "Order confirmation",     blurb: "Sent when Stripe confirms payment. Line items, thumbnails and totals are auto-rendered." },
  order_shipped:         { title: "Order shipped",          blurb: "Sent when you mark an order as Shipped in the Orders tab. Tracking number auto-rendered if present." },
  order_delivered:       { title: "Order delivered",        blurb: "Sent when you mark an order as Delivered. Great moment to ask for a review." },
  order_refund:          { title: "Order refund",           blurb: "Sent when you refund an order from the Orders tab." },
  password_reset:        { title: "Password reset",         blurb: "Sent when a user clicks 'Forgot password?' in the login modal." },
  waitlist_confirmation: { title: "Waitlist joined",        blurb: "Sent when a shopper joins a waitlist on an out-of-stock item." },
  waitlist_restock:      { title: "Back in stock",          blurb: "Auto-sent when a variant's stock flips from 0 → positive (via product edit or refund restock)." },
};

const EmailCard = ({ item, locale, onLocalUpdate, onLocalReset }) => {
  const { key, defaults, current, variables } = item;
  const [subject, setSubject] = useState(current.subject);
  const [heading, setHeading] = useState(current.heading);
  const [intro, setIntro] = useState(current.intro);
  const [ctaLabel, setCtaLabel] = useState(current.cta_label || "");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);

  // Re-sync inputs when the selected locale changes (parent passes a new item)
  useEffect(() => {
    setSubject(current.subject);
    setHeading(current.heading);
    setIntro(current.intro);
    setCtaLabel(current.cta_label || "");
    setMsg("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, key]);

  const dirty =
    subject !== current.subject ||
    heading !== current.heading ||
    intro !== current.intro ||
    (ctaLabel || "") !== (current.cta_label || "");

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await adminUpdateEmailTemplate(key, { subject, heading, intro, cta_label: ctaLabel }, locale);
      setMsg(`Saved ✓ (${locale.toUpperCase()})`);
      onLocalUpdate?.(key, { subject, heading, intro, cta_label: ctaLabel });
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${key} (${locale.toUpperCase()}) to the default copy? Your customisations for this language will be lost.`)) return;
    setSaving(true); setMsg("");
    try {
      await adminResetEmailTemplate(key, locale);
      setSubject(defaults.subject);
      setHeading(defaults.heading);
      setIntro(defaults.intro);
      setCtaLabel(defaults.cta_label || "");
      setMsg(`Reset to default ✓ (${locale.toUpperCase()})`);
      onLocalReset?.(key, defaults);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { setMsg("Enter a test email address first"); return; }
    setTesting(true); setMsg("");
    try {
      // Save latest edits first so the test reflects what's in the fields
      if (dirty) {
        await adminUpdateEmailTemplate(key, { subject, heading, intro, cta_label: ctaLabel }, locale);
        onLocalUpdate?.(key, { subject, heading, intro, cta_label: ctaLabel });
      }
      await adminSendTestEmail(key, testTo.trim(), locale);
      setMsg(`Test sent to ${testTo.trim()} in ${locale.toUpperCase()} ✓`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  const label = LABELS[key] || { title: key, blurb: "" };

  return (
    <section
      className="bg-white border border-[#F1DCC5] rounded-2xl overflow-hidden"
      data-testid={`admin-email-card-${key}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between p-5 md:p-6 text-left hover:bg-[#FFF8E9] transition"
        data-testid={`admin-email-toggle-${key}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif-display text-xl md:text-2xl text-[#241C18]">{label.title}</span>
            {item.customised && (
              <span className="font-badge text-[9px] tracking-[0.24em] bg-[var(--gl-orange)] text-[var(--gl-orange-text)] px-2 py-0.5 rounded-full">
                CUSTOMISED
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs text-[#8A7568] max-w-2xl">{label.blurb}</p>
          <div className="mt-2 text-xs font-typewriter text-[#241C18] truncate max-w-full">
            Subject: <span className="font-badge tracking-wide">{subject}</span>
          </div>
        </div>
        <span className="ml-4 shrink-0 text-[#8A7568] text-lg pt-1">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="border-t border-[#F1DCC5] p-5 md:p-6 grid gap-5 bg-[#FDFBF6]">
          <div>
            <label className="block text-[10px] font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1.5">
              Subject line
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
              data-testid={`admin-email-subject-${key}`}
            />
          </div>

          <div>
            <label className="block text-[10px] font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1.5">
              Heading (H1 in the email)
            </label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="w-full px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
              data-testid={`admin-email-heading-${key}`}
            />
          </div>

          <div>
            <label className="block text-[10px] font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1.5">
              Intro paragraph
            </label>
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm leading-relaxed"
              data-testid={`admin-email-intro-${key}`}
            />
            {variables?.length > 0 && (
              <div className="mt-2 text-[11px] text-[#8A7568] font-typewriter">
                Available placeholders:{" "}
                {variables.map((v) => (
                  <code key={v} className="mx-1 px-1.5 py-0.5 bg-[#FFE9D6] rounded">{`{${v}}`}</code>
                ))}
              </div>
            )}
          </div>

          {(defaults.cta_label || ctaLabel) && (
            <div>
              <label className="block text-[10px] font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1.5">
                CTA button label (leave empty to hide)
              </label>
              <input
                type="text"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder={defaults.cta_label || ""}
                className="w-full px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
                data-testid={`admin-email-cta-${key}`}
              />
            </div>
          )}

          <div className="border-t border-[#F1DCC5] pt-4">
            <label className="block text-[10px] font-badge tracking-[0.24em] uppercase text-[#8A7568] mb-1.5">
              Send a live test
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                type="email"
                placeholder="your@email.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="flex-1 min-w-[220px] px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm"
                data-testid={`admin-email-test-input-${key}`}
              />
              <button
                type="button"
                onClick={sendTest}
                disabled={testing}
                className="px-4 py-2 bg-[var(--gl-orange)] text-[var(--gl-orange-text)] rounded text-xs font-badge tracking-[0.2em] uppercase hover:bg-[var(--gl-orange-hover)] hover:text-white transition disabled:opacity-50"
                data-testid={`admin-email-test-send-${key}`}
              >
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="px-5 py-2 bg-[#241C18] text-[var(--gl-orange)] rounded text-xs font-badge tracking-[0.2em] uppercase disabled:opacity-40"
              data-testid={`admin-email-save-${key}`}
            >
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
            {item.customised && (
              <button
                type="button"
                onClick={reset}
                className="px-3 py-2 border border-[#241C18] rounded text-xs font-badge tracking-[0.2em] uppercase text-[#241C18] hover:bg-[#241C18] hover:text-white transition"
                data-testid={`admin-email-reset-${key}`}
              >
                Reset to default
              </button>
            )}
            {msg && (
              <span className="text-xs text-[#4a7a2c] font-typewriter" data-testid={`admin-email-msg-${key}`}>
                {msg}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const AdminEmails = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await adminListEmailTemplates();
      setItems(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load email templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Update a single card's `current` + `customised` flag locally so the
  // card doesn't remount and auto-collapse after Save.
  const onLocalUpdate = (key, patch) => {
    setItems((prev) => prev.map((it) => it.key === key
      ? { ...it, current: { ...it.current, ...patch }, customised: true }
      : it));
  };
  const onLocalReset = (key, defaults) => {
    setItems((prev) => prev.map((it) => it.key === key
      ? { ...it, current: { ...defaults }, customised: false }
      : it));
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-emails-tab">
      <div className="mb-8">
        <h2 className="font-serif-display text-3xl md:text-4xl text-[#241C18]">Transactional emails</h2>
        <p className="mt-2 text-sm text-[#8A7568] max-w-2xl">
          Edit the subject line, heading and intro paragraph for every automatic email the shop sends.
          The GOYA header, order tables, tracking blocks and buttons stay branded — you just control the words.
          Use <code className="font-typewriter bg-[#FFE9D6] px-1 rounded">{`{placeholders}`}</code> for dynamic bits.
          Empty subject/heading/intro fields fall back to the built-in default.
        </p>
      </div>

      {loading && <div className="text-sm text-[#8A7568]">Loading templates…</div>}
      {error && <div className="text-sm text-[#C97A63]">{error}</div>}

      <div className="grid gap-3">
        {items.map((item) => (
          <EmailCard key={item.key} item={item} onLocalUpdate={onLocalUpdate} onLocalReset={onLocalReset} />
        ))}
      </div>
    </div>
  );
};

export default AdminEmails;
